import { useMemo, useState } from "react";
import { t } from "@/i18n-js/instance";
import { Form } from "@/react/components/shared/uikit/Form";
import { Loader } from "@/react/components/shared/uikit/Loader";
import { useSpacesApi } from "@/react/hooks/useSpacesApi";
import type { Space } from "@/react/types/Space";
import { usePagesData } from "@circle-react/components/BrandedAppsBuilderUI/hooks/usePagesData";
import { FormItem } from "@circle-react/components/SettingsApp/General/FormItem";
import { useBrandedAppsBuilderFeatureFlagUI } from "@circle-react/hooks/useBrandedAppsBuilderUIFeatureFlag";
import { Button } from "@circle-react-shared/uikit/Button";
import { Typography } from "@circle-react-uikit/Typography";
import { FormDivider } from "../FormDivider";
import type { WelcomeDataPutPayload } from "./useWelcomeDataApi";
import { useWelcomeDataApi } from "./useWelcomeDataApi";
import { useWelcomeSpacesApi } from "./useWelcomeSpaces";

const i18nRoot = "settings_nav_v3.branded_apps_dashboard.welcome_space";

const NO_SPACE_OPTION = {
  label: t("settings_nav_v3.branded_apps_dashboard.welcome_space.no_space"),
  value: "",
  group: "",
  leftAddon: "",
};

const GROUP_SPACES = t(
  "settings_nav_v3.branded_apps_dashboard.welcome_space.spaces",
);
const GROUP_SCREENS = t(
  "settings_nav_v3.branded_apps_dashboard.welcome_space.screens",
);

interface WelcomeSpaceFormProps {
  new_member_space_id: string;
  regular_member_space_id: string;
}

export const WelcomeSpaceForm = () => {
  const { data, isLoading: isSpacesLoading } = useSpacesApi();
  const {
    data: welcomeDataResponse,
    isLoading: isWelcomeDataLoading,
    updateWelcomeSpaces: updateWelcomeData,
  } = useWelcomeDataApi();
  const { homeTabsPages } = usePagesData({ status: "published" });
  const { isWelcomePagesHomeTabsEnabled } =
    useBrandedAppsBuilderFeatureFlagUI();

  // TODO: Remove after feature flag welcome_page_home_tabs_enabled is removed
  const {
    data: welcomeSpaceData,
    isLoading: isWelcomeSpacesLoading,
    updateWelcomeSpaces,
  } = useWelcomeSpacesApi();

  const [isSaving, setIsSaving] = useState(false);

  const welcomeData = welcomeDataResponse?.welcome_data;
  const welcomeSpaces = welcomeSpaceData?.welcome_spaces;

  const onSubmit = async (formData: WelcomeSpaceFormProps) => {
    setIsSaving(true);
    try {
      if (isWelcomePagesHomeTabsEnabled) {
        const payload = convertFormIntoWelcomeDataPayload(formData);
        await updateWelcomeData(payload);
      } else {
        await updateWelcomeSpaces(formData);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const welcomePagesOptions = useMemo(() => {
    const spaces = data?.records || [];

    const mappedSpaces = spaces.map((space: Space) => ({
      label: space.name,
      value: String(space.id),
      group: GROUP_SPACES,
      leftAddon: space.emoji || "",
    }));

    const mappedHomeTabs = Object.values(homeTabsPages || {})
      .filter(page => !page.meta?.isInNav && !page.meta?.isDefaultPage)
      .map(page => ({
        label:
          (page.meta?.custom_name as string) || (page.meta?.kind as string),
        value: page.id,
        group: GROUP_SCREENS,
        leftAddon: "",
      }));

    return isWelcomePagesHomeTabsEnabled
      ? [NO_SPACE_OPTION, ...mappedHomeTabs, ...mappedSpaces]
      : [NO_SPACE_OPTION, ...mappedSpaces];
  }, [data?.records, homeTabsPages, isWelcomePagesHomeTabsEnabled]);

  const convertFormIntoWelcomeDataPayload = (
    form: WelcomeSpaceFormProps,
  ): WelcomeDataPutPayload => {
    const newMemberOption = welcomePagesOptions.find(
      o => o.value === form.new_member_space_id,
    );
    const regularMemberOption = welcomePagesOptions.find(
      o => o.value === form.regular_member_space_id,
    );
    const payload: WelcomeDataPutPayload = {
      welcome_data: {
        spaces: { new_member_space_id: null, regular_member_space_id: null },
        screens: { new_member_screen_id: null, regular_member_screen_id: null },
      },
    };

    if (newMemberOption) {
      if (newMemberOption.group === GROUP_SPACES) {
        payload.welcome_data.spaces.new_member_space_id = Number(
          newMemberOption.value,
        );
      } else if (newMemberOption.group === GROUP_SCREENS) {
        payload.welcome_data.screens.new_member_screen_id =
          newMemberOption.value;
      }
    }

    if (regularMemberOption) {
      if (regularMemberOption.group === GROUP_SPACES) {
        payload.welcome_data.spaces.regular_member_space_id = Number(
          regularMemberOption.value,
        );
      } else if (regularMemberOption.group === GROUP_SCREENS) {
        payload.welcome_data.screens.regular_member_screen_id =
          regularMemberOption.value;
      }
    }

    return payload;
  };

  const getIdString = (a?: number | string | null) =>
    a == null ? "" : String(a);

  // TODO: remove the useMemo when the feature flag is removed.
  const formDefaultData = useMemo(() => {
    const getValidOption = (value: string) =>
      welcomePagesOptions.some(option => option.value === value)
        ? value
        : NO_SPACE_OPTION.value;

    if (isWelcomePagesHomeTabsEnabled) {
      const newMemberId = getIdString(
        welcomeData?.spaces?.new_member_space_id ||
          welcomeData?.screens?.new_member_screen_id,
      );
      const regularMemberId = getIdString(
        welcomeData?.spaces?.regular_member_space_id ||
          welcomeData?.screens?.regular_member_screen_id,
      );
      return {
        new_member_space_id: getValidOption(newMemberId),
        regular_member_space_id: getValidOption(regularMemberId),
      };
    }

    const newMemberId = getIdString(welcomeSpaces?.new_member_space_id);
    const regularMemberId = getIdString(welcomeSpaces?.regular_member_space_id);
    return {
      new_member_space_id: getValidOption(newMemberId),
      regular_member_space_id: getValidOption(regularMemberId),
    };
  }, [
    isWelcomePagesHomeTabsEnabled,
    welcomeData?.screens?.new_member_screen_id,
    welcomeData?.screens?.regular_member_screen_id,
    welcomeData?.spaces?.new_member_space_id,
    welcomeData?.spaces?.regular_member_space_id,
    welcomeSpaces?.new_member_space_id,
    welcomeSpaces?.regular_member_space_id,
    welcomePagesOptions,
  ]);

  if (isSpacesLoading || isWelcomeDataLoading || isWelcomeSpacesLoading) {
    return <Loader />;
  }

  return (
    <div>
      <div className="flex">
        <div className="flex-1">
          <div className="border-primary flex justify-between">
            <Typography.TitleSm>
              {t("settings_nav_v3.branded_apps_dashboard.welcome_space.title")}
            </Typography.TitleSm>
          </div>
        </div>
      </div>
      <FormDivider />

      <div>
        <Form
          onSubmit={(formData: WelcomeSpaceFormProps) => onSubmit(formData)}
          defaultValues={formDefaultData}
        >
          <div className="flex">
            <FormItem
              id="new_member_space_id"
              name="new_member_space_id"
              isHorizontal
              className="!flex w-full"
              translationRoot={i18nRoot}
            >
              <Form.TextSelect
                name="new_member_space_id"
                options={welcomePagesOptions}
                label=""
                groupBy={(option: typeof NO_SPACE_OPTION) => option.group}
                initialValue={formDefaultData.new_member_space_id}
              />
            </FormItem>
          </div>
          <div>
            <FormItem
              id="regular_member_space_id"
              name="regular_member_space_id"
              isHorizontal
              className="!flex w-full"
              translationRoot={i18nRoot}
            >
              <Form.TextSelect
                label=""
                name="regular_member_space_id"
                options={welcomePagesOptions}
                groupBy={(option: typeof NO_SPACE_OPTION) => option.group}
                initialValue={formDefaultData.regular_member_space_id}
              />
            </FormItem>
          </div>
          <div className="mt-6 flex justify-end">
            <Button type="submit" variant="circle" disabled={isSaving}>
              {isSaving ? t("saving") : t("save_changes")}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};
